import { Box, Grid } from '@chakra-ui/react';
import {
  ReactZoomPanPinchRef,
  TransformComponent,
  TransformWrapper
} from '@pronestor/react-zoom-pan-pinch';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  calcZoomScaleForHeight,
  calcZoomScaleForWidth,
  nearStation,
  revertX,
  revertY,
  translateX,
  translateY,
  VBHeight_Overview,
  VBWidth_Overview
} from '../common/svg';
import Controls from '../components/organisms/Controls';
import OverviewMap from '../components/organisms/OverviewMap';
import { useZoomState } from '../hooks/useZoomState';
import { WindowSizeContext } from '../context/WindowSizeContextProvider';
import { MasterContext } from '../context/MasterContextProvider';
import { URLs } from '../RouterConfig';
import Message from '../components/organisms/Message';

const Overview: React.FC = () => {
  const zoomRef = useRef<ReactZoomPanPinchRef>(null); //react-zoom-pan-pinchの状態を取得するためのref
  const navigate = useNavigate();
  const { master } = useContext(MasterContext);
  const { size } = useContext(WindowSizeContext);

  // 遷移を発生させる倍率
  const borderScale = useMemo(
    () =>
      calcZoomScaleForWidth(size, VBWidth_Overview, VBHeight_Overview, 200, 6),
    [size]
  );

  // マップ画像の余白がなくなるズーム倍率を最小とする
  const minScale = useMemo(
    () =>
      Math.min(
        calcZoomScaleForWidth(
          size,
          VBWidth_Overview,
          VBHeight_Overview,
          720, //VBWidth_Overview(1120) - 画像の余白(400)
          10
        ),
        calcZoomScaleForHeight(
          size,
          VBWidth_Overview,
          VBHeight_Overview,
          1280,
          10
        )
      ),
    [size]
  );

  // 初期フォーカス位置とズーム倍率
  const location = useLocation();
  const stationNo = location.state?.stationNo ?? null;
  const initialScale = stationNo ? borderScale : minScale;

  const initialPositionX = useMemo(
    () =>
      translateX(
        size,
        master.stations.find((station) => station.station_no === stationNo)
          ?.x_overview ?? 0,
        VBWidth_Overview,
        VBHeight_Overview,
        borderScale
      ),
    [borderScale, master.stations, size, stationNo]
  );
  const initialPositionY = useMemo(
    () =>
      translateY(
        size,
        master.stations.find((station) => station.station_no === stationNo)
          ?.y_overview ?? 0,
        VBWidth_Overview,
        VBHeight_Overview,
        borderScale
      ),
    [borderScale, master.stations, size, stationNo]
  );

  const { zoomState, setZoommState } = useZoomState(
    zoomRef.current?.state.scale ?? initialScale,
    zoomRef.current?.state.previousScale ?? initialScale,
    zoomRef.current?.state.positionX ?? 0,
    zoomRef.current?.state.positionY ?? 0
  );

  // ズーム操作時にzoomStateを更新する関数
  const updateZoomState = useCallback(() => {
    setZoommState({
      scale: zoomRef.current?.state.scale ?? initialScale,
      previousScale: zoomRef.current?.state.previousScale ?? initialScale,
      positionX: zoomRef.current?.state.positionX ?? 0,
      positionY: zoomRef.current?.state.positionY ?? 0
    });
  }, [initialScale, setZoommState]);

  // 拡大率が一定を超えたとき、画面の中心に最も近い電停に遷移
  useEffect(() => {
    if (zoomState.scale > borderScale) {
      const _station_no = nearStation(
        revertX(
          zoomState.positionX,
          size,
          VBWidth_Overview,
          VBHeight_Overview,
          zoomState.scale
        ),
        revertY(
          zoomState.positionY,
          size,
          VBWidth_Overview,
          VBHeight_Overview,
          zoomState.scale
        ),
        master.stations,
        'overview'
      );
      navigate(`/${URLs.zoomMap}/${_station_no}`);
    }
  }, [
    borderScale,
    master.stations,
    navigate,
    size,
    zoomState.positionX,
    zoomState.positionY,
    zoomState.scale
  ]);

  return (
    <>
      {/* 全体領域をWrapperで囲む */}
      <TransformWrapper
        initialScale={initialScale}
        initialPositionX={initialPositionX}
        initialPositionY={initialPositionY}
        minScale={minScale}
        maxScale={borderScale + 0.01}
        ref={zoomRef}
        onZoom={updateZoomState}
        centerOnInit={stationNo ? false : true}
      >
        {({ centerView }) => (
          <Grid
            w={size.w}
            h={size.h}
            gridTemplateAreas={`'header' 'map' 'footer'`}
            gridTemplateRows='5% 88% 7%'
          >
            <Box gridArea={'header'}>
              <Message></Message>
            </Box>
            {/* 独立してズームしたい領域をComponentで囲む */}
            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%',
                backgroundColor: 'black',
                gridArea: 'map'
              }}
              contentStyle={{
                width: '100%',
                height: '100%'
              }}
            >
              <OverviewMap zoomScale={zoomState.scale}></OverviewMap>
            </TransformComponent>
            <Box gridArea={'footer'}>
              <Controls
                reset={() => {
                  centerView(minScale);
                  setZoommState({
                    scale: minScale,
                    previousScale: minScale,
                    positionX: 0,
                    positionY: 0
                  });
                }}
                toCurrent={(stationNo) => {
                  navigate(`/${URLs.zoomMap}/${stationNo}`);
                }}
              ></Controls>
            </Box>
          </Grid>
        )}
      </TransformWrapper>
    </>
  );
};

export default React.memo(Overview);
